import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileClock, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BASE, api, getToken } from '@/lib/api';
import { usePageParams } from '@/hooks/usePageParams';

type PurchaseOrderLine = {
  id: string;
  line_number: number;
  sku_code: string | null;
  sku_name: string | null;
  qty_ordered: number;
  qty_fulfilled: number;
  uom: string | null;
  unit_price: number | null;
  amount: number | null;
  notes: string | null;
  last_seen_version: number | null;
  removed_in_version: number | null;
  exception_status: string | null;
  exception_note: string | null;
  status: string | null;
  material_group?: string | null;
  mpn?: string | null;
  req_no?: string | null;
  requested_by?: string | null;
  delivery_date?: string | null;
  raw_line_amount?: number | string | null;
  metadata?: Record<string, unknown> | null;
};

type PurchaseOrderImportHistory = {
  id: string;
  attachment_id?: string | null;
  version_number: number;
  file_name: string | null;
  rows_total: number;
  rows_added: number;
  rows_updated: number;
  rows_removed: number;
  rows_exception: number;
  import_status: string | null;
  created_at: string | null;
};

type PurchaseOrderFulfillment = {
  id: string;
  fulfillment_type: string | null;
  reference_number: string | null;
  qty_fulfilled: number;
  created_at: string | null;
};

type PurchaseOrderDetail = {
  id: string;
  order_number: string;
  counterparty_name: string | null;
  project_code: string | null;
  order_date: string | null;
  currency: string | null;
  vendor_ref?: string | null;
  buyer?: string | null;
  terms_of_payment?: string | null;
  deliver_to?: string | null;
  supplier_address?: string | null;
  current_version: number;
  line_exception_count: number;
  total_qty: number;
  total_fulfilled: number;
  required_date: string | null;
  status: string;
  created_at: string | null;
  metadata?: Record<string, unknown> | null;
  lines: PurchaseOrderLine[];
  fulfillments: PurchaseOrderFulfillment[];
  importHistory: PurchaseOrderImportHistory[];
};

const PO_MANUAL_EDIT_ENABLED = false;

function toneForStatus(status: string | null | undefined) {
  switch (status) {
    case 'CONFIRMED':
      return 'bg-blue-100 text-blue-700';
    case 'COMPLETED':
    case 'CLOSED':
      return 'bg-green-100 text-green-700';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    case 'EXCEPTION':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function formatStatusLabel(status: string | null | undefined) {
  if (!status) return 'Unknown';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function toneForException(status: string | null | undefined) {
  switch (status) {
    case 'OVER_RECEIVED':
      return 'bg-red-100 text-red-700';
    case 'REMOVED_WITH_FULFILLMENT':
      return 'bg-orange-100 text-orange-700';
    case 'REMOVED':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function toDateInput(value: string | null | undefined) {
  if (!value || value === '-') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function getPurchaseOrderImportSection(metadata: unknown, section: 'header' | 'line'): Record<string, unknown> {
  return asRecord(asRecord(asRecord(metadata).po_import)[section]);
}

function formatScalar(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  return '-';
}

function parseNumberField(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char ?? '';
  }

  row.push(cell);
  if (row.some((value) => value.trim() !== '') || rows.length === 0) rows.push(row);
  return rows;
}

type WmsPurchaseOrderDetailPanelProps = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
};

export function WmsPurchaseOrderDetailPanel({
  id: idProp,
  embedded = false,
  onClose,
}: WmsPurchaseOrderDetailPanelProps) {
  const navigate = useNavigate();
  const params = usePageParams<{ id: string }>();
  const id = idProp ?? params.id;
  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [headerSaving, setHeaderSaving] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [lineValues, setLineValues] = useState<Record<string, string>>({});
  const [csvViewer, setCsvViewer] = useState<{
    open: boolean;
    title: string;
    headers: string[];
    rows: string[][];
    loading: boolean;
    error: string | null;
  }>({ open: false, title: 'CSV Source', headers: [], rows: [], loading: false, error: null });

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await api.get<PurchaseOrderDetail>(`/wms/business-orders/${id}`);
      setOrder(detail);
      setDraftNotes(Object.fromEntries((detail.lines ?? []).map((line) => [line.id, line.exception_note ?? ''])));
      setHeaderValues({
        orderNumber: detail.order_number ?? '',
        counterpartyName: detail.counterparty_name ?? '',
        projectCode: detail.project_code ?? '',
        orderDate: toDateInput(detail.order_date),
        currency: detail.currency ?? '',
        requiredDate: toDateInput(detail.required_date),
        vendorRef: detail.vendor_ref ?? '',
        buyer: detail.buyer ?? '',
        termsOfPayment: detail.terms_of_payment ?? '',
        deliverTo: detail.deliver_to ?? '',
        supplierAddress: detail.supplier_address ?? '',
        notes: String(detail.metadata?.notes ?? ''),
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  const closeOrBack = () => {
    if (embedded) {
      onClose?.();
      return;
    }
    navigate('/wms/purchase-orders');
  };

  const saveHeader = async () => {
    if (!order) return;
    setHeaderSaving(true);
    try {
      await api.patch(`/wms/business-orders/${order.id}`, {
        orderNumber: headerValues.orderNumber,
        counterpartyName: headerValues.counterpartyName,
        projectCode: headerValues.projectCode,
        orderDate: headerValues.orderDate,
        currency: headerValues.currency,
        requiredDate: headerValues.requiredDate,
        vendorRef: headerValues.vendorRef,
        buyer: headerValues.buyer,
        termsOfPayment: headerValues.termsOfPayment,
        deliverTo: headerValues.deliverTo,
        supplierAddress: headerValues.supplierAddress,
      });
      toast.success('PO header updated');
      setEditingHeader(false);
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update PO header');
    } finally {
      setHeaderSaving(false);
    }
  };

  const startLineEdit = (line: PurchaseOrderLine) => {
    const rawLine = getPurchaseOrderImportSection(line.metadata, 'line');
    setEditingLineId(line.id);
    setLineValues({
      skuCode: line.sku_code ?? '',
      skuName: line.sku_name ?? '',
      qtyOrdered: String(line.qty_ordered ?? ''),
      uom: line.uom ?? '',
      unitPrice: line.unit_price == null ? '' : String(line.unit_price),
      materialGroup: line.material_group ?? formatScalar(rawLine.material_group).replace('-', ''),
      mpn: line.mpn ?? formatScalar(rawLine.mpn).replace('-', ''),
      reqNo: line.req_no ?? formatScalar(rawLine.req_no).replace('-', ''),
      requestedBy: line.requested_by ?? formatScalar(rawLine.requested_by).replace('-', ''),
      deliveryDate: toDateInput(line.delivery_date ?? formatScalar(rawLine.delivery_date)),
      notes: line.notes ?? '',
    });
  };

  const saveLine = async (line: PurchaseOrderLine) => {
    setSavingLineId(line.id);
    try {
      await api.patch(`/wms/business-order-lines/${line.id}`, {
        skuCode: lineValues.skuCode,
        skuName: lineValues.skuName,
        qtyOrdered: parseNumberField(lineValues.qtyOrdered ?? ''),
        uom: lineValues.uom,
        unitPrice: parseNumberField(lineValues.unitPrice ?? ''),
        materialGroup: lineValues.materialGroup,
        mpn: lineValues.mpn,
        reqNo: lineValues.reqNo,
        requestedBy: lineValues.requestedBy,
        deliveryDate: lineValues.deliveryDate,
        notes: lineValues.notes,
      });
      toast.success(`PO line ${line.line_number} updated`);
      setEditingLineId(null);
      setLineValues({});
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update PO line');
    } finally {
      setSavingLineId(null);
    }
  };

  const resolveException = async (line: PurchaseOrderLine) => {
    setSavingLineId(line.id);
    try {
      await api.patch(`/wms/business-order-lines/${line.id}`, {
        exceptionStatus: 'NONE',
        exceptionNote: draftNotes[line.id] ?? line.exception_note ?? '',
      });
      toast.success(`PO line ${line.line_number} resolved`);
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to resolve PO exception');
    } finally {
      setSavingLineId(null);
    }
  };

  const openHistoryCsv = async (item: PurchaseOrderImportHistory) => {
    if (!item.attachment_id) {
      toast.error('CSV source is not available for this import');
      return;
    }

    setCsvViewer({
      open: true,
      title: item.file_name || `PO version ${item.version_number} CSV`,
      headers: [],
      rows: [],
      loading: true,
      error: null,
    });
    try {
      const token = getToken();
      const response = await fetch(`${BASE}/wms/attachments/${encodeURIComponent(item.attachment_id)}/content`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const text = await response.text();
      const [headers = [], ...rows] = parseCsvRows(text);
      setCsvViewer({
        open: true,
        title: item.file_name || `PO version ${item.version_number} CSV`,
        headers,
        rows,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      setCsvViewer({
        open: true,
        title: item.file_name || `PO version ${item.version_number} CSV`,
        headers: [],
        rows: [],
        loading: false,
        error: err?.message ?? 'Failed to open CSV source',
      });
    }
  };

  const downloadHistoryCsv = async (item: PurchaseOrderImportHistory) => {
    if (!item.attachment_id) {
      toast.error('CSV source is not available for this import');
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${BASE}/wms/attachments/${encodeURIComponent(item.attachment_id)}/content`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.file_name || `po-v${item.version_number}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to download CSV source');
    }
  };

  const saveExceptionNote = async (line: PurchaseOrderLine) => {
    setSavingLineId(line.id);
    try {
      await api.patch(`/wms/business-order-lines/${line.id}`, {
        exceptionStatus: line.exception_status ?? undefined,
        exceptionNote: draftNotes[line.id] ?? '',
      });
      toast.success(`PO line ${line.line_number} note updated`);
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update exception note');
    } finally {
      setSavingLineId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-4 p-4">
        <Button variant="outline" size="sm" onClick={closeOrBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {embedded ? 'Close' : 'Back to PO'}
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Purchase order not found
          </CardContent>
        </Card>
      </div>
    );
  }

  const rawHeader = getPurchaseOrderImportSection(order.metadata, 'header');
  const rawOrNormalized = (rawValue: unknown, normalizedValue: string | number | null | undefined) => {
    const rawText = formatScalar(rawValue);
    return rawText !== '-' ? rawText : (normalizedValue || '-');
  };

  const renderHeaderField = (
    key: string,
    label: string,
    value: string | number | null | undefined,
    type: 'text' | 'date' | 'textarea' = 'text',
  ) => (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      {PO_MANUAL_EDIT_ENABLED && editingHeader ? (
        type === 'textarea' ? (
          <Textarea
            className="mt-1 text-sm"
            rows={3}
            value={headerValues[key] ?? ''}
            onChange={(event) => setHeaderValues((prev) => ({ ...prev, [key]: event.target.value }))}
          />
        ) : (
          <Input
            className="mt-1 h-9 text-sm"
            type={type}
            value={headerValues[key] ?? ''}
            onChange={(event) => setHeaderValues((prev) => ({ ...prev, [key]: event.target.value }))}
          />
        )
      ) : (
        <div className="mt-1 text-sm font-medium break-words">{value || '-'}</div>
      )}
    </div>
  );

  return (
    <>
    <div className="flex min-h-0 min-w-0 h-full flex-col overflow-hidden p-4">
      <div className="mb-4 shrink-0 space-y-1">
        {!embedded ? (
          <Button variant="ghost" size="sm" className="-ml-2" onClick={closeOrBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to PO
          </Button>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight">Purchase Order : {order.order_number}</h1>
          <Badge variant="outline" className={toneForStatus(order.status)}>
            {formatStatusLabel(order.status)}
          </Badge>
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
      <div className="flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-y-auto pr-1">
      <Card className="min-w-0">
        {PO_MANUAL_EDIT_ENABLED ? (
        <CardHeader className="flex flex-row items-center justify-end gap-3">
            <div className="flex items-center gap-2">
              {editingHeader ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setEditingHeader(false)} disabled={headerSaving}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => void saveHeader()} disabled={headerSaving}>
                    {headerSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save Header
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditingHeader(true)}>
                  Edit Header
                </Button>
              )}
            </div>
        </CardHeader>
          ) : null}
        <CardContent className="grid gap-3 pt-1 md:grid-cols-2 xl:grid-cols-6">
          {renderHeaderField('orderNumber', 'PO No', rawOrNormalized(rawHeader.po_number_raw, order.order_number))}
          {renderHeaderField('counterpartyName', 'Supplier', order.counterparty_name)}
          {renderHeaderField('projectCode', 'Project No', rawOrNormalized(rawHeader.project_no_raw, order.project_code))}
          {renderHeaderField('orderDate', 'PO Date', rawOrNormalized(rawHeader.po_date, formatDate(order.order_date)), 'date')}
          {renderHeaderField('currency', 'Currency', rawOrNormalized(rawHeader.currency, order.currency))}
          {renderHeaderField('requiredDate', 'Delivery Date', rawOrNormalized(rawHeader.delivery_date, formatDate(order.required_date)), 'date')}
          {renderHeaderField('vendorRef', 'Vendor Ref', rawOrNormalized(rawHeader.vendor_ref, order.vendor_ref))}
          {renderHeaderField('buyer', 'Buyer', rawOrNormalized(rawHeader.buyer, order.buyer))}
          {renderHeaderField('termsOfPayment', 'Terms of Payment', rawOrNormalized(rawHeader.terms_of_payment, order.terms_of_payment))}
          {renderHeaderField('deliverTo', 'Deliver To', rawOrNormalized(rawHeader.deliver_to, order.deliver_to))}
          <div className="md:col-span-2">
            {renderHeaderField('supplierAddress', 'Supplier Address', rawOrNormalized(rawHeader.supplier_address, order.supplier_address), 'textarea')}
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-0 min-w-0 overflow-hidden">
        <CardContent className="min-w-0 space-y-3 pt-1">
          {order.lines.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No PO lines found.
            </div>
          ) : (
            <div className="runtime-grid-scrollbar boox-x-scroll max-h-[min(58vh,760px)] min-w-0 overflow-auto rounded-lg border">
              <table className="w-max min-w-[1560px] text-sm">
                <thead className="sticky top-0 z-10 bg-background shadow-sm">
                  <tr>
                    <th className="bg-background px-3 py-2 text-left">Line</th>
                    <th className="bg-background px-3 py-2 text-left">Material Code</th>
                    <th className="bg-background px-3 py-2 text-left">Description</th>
                    <th className="bg-background px-3 py-2 text-left">Material Group</th>
                    <th className="bg-background px-3 py-2 text-left">MPN</th>
                    <th className="bg-background px-3 py-2 text-left">Req No</th>
                    <th className="bg-background px-3 py-2 text-left">Requested By</th>
                    <th className="bg-background px-3 py-2 text-left">Delivery</th>
                    <th className="bg-background px-3 py-2 text-right">Qty Ordered</th>
                    <th className="bg-background px-3 py-2 text-left">UOM</th>
                    <th className="bg-background px-3 py-2 text-right">Unit Price</th>
                    <th className="bg-background px-3 py-2 text-right">Amount</th>
                    <th className="bg-background px-3 py-2 text-left">Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((line) => {
                    const rawLine = getPurchaseOrderImportSection(line.metadata, 'line');
                    const isEditing = PO_MANUAL_EDIT_ENABLED && editingLineId === line.id;
                    const materialGroup = line.material_group ?? formatScalar(rawLine.material_group);
                    const mpn = line.mpn ?? formatScalar(rawLine.mpn);
                    const reqNo = line.req_no ?? formatScalar(rawLine.req_no);
                    const requestedBy = line.requested_by ?? formatScalar(rawLine.requested_by);
                    const deliveryDate = line.delivery_date ?? formatScalar(rawLine.delivery_date);
                    const isPipeLine = [
                      line.sku_name,
                      line.sku_code,
                      materialGroup,
                      rawLine.material_group,
                      rawLine.description,
                    ].some((value) => String(value ?? '').toUpperCase().includes('PIPE'));

                    return (
                    <tr key={line.id} className={`border-t align-top ${isEditing ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-3 py-2 font-medium">{line.line_number}</td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" value={lineValues.skuCode ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, skuCode: event.target.value }))} />
                        ) : line.sku_code || '-'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="space-y-1">
                          {isEditing ? (
                            <>
                              <Input className="h-8 text-xs" value={lineValues.skuName ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, skuName: event.target.value }))} />
                              <Textarea className="mt-2 min-h-[56px] text-xs" value={lineValues.notes ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, notes: event.target.value }))} />
                            </>
                          ) : (
                            <>
                              <div>{line.sku_name || '-'}</div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" value={lineValues.materialGroup ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, materialGroup: event.target.value }))} />
                        ) : materialGroup}
                      </td>
                      <td className="px-3 py-2">{mpn}</td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" value={lineValues.reqNo ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, reqNo: event.target.value }))} />
                        ) : reqNo}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" value={lineValues.requestedBy ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, requestedBy: event.target.value }))} />
                        ) : requestedBy}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" type="date" value={lineValues.deliveryDate ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, deliveryDate: event.target.value }))} />
                        ) : deliveryDate !== '-' ? formatDate(deliveryDate) : '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <Input className="h-8 text-xs text-right" type="number" value={lineValues.qtyOrdered ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, qtyOrdered: event.target.value }))} />
                        ) : line.qty_ordered ?? 0}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input className="h-8 text-xs" value={lineValues.uom ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, uom: event.target.value }))} />
                        ) : line.uom || '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <Input className="h-8 text-xs text-right" type="number" value={lineValues.unitPrice ?? ''} onChange={(event) => setLineValues((prev) => ({ ...prev, unitPrice: event.target.value }))} />
                        ) : isPipeLine ? '-' : (line.unit_price ?? '-')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isPipeLine ? '-' : (line.amount ?? '-')}
                      </td>
                      <td className="px-3 py-2">
                        <div className="space-y-2">
                          {PO_MANUAL_EDIT_ENABLED ? (
                            <div className="flex flex-wrap gap-2">
                              {isEditing ? (
                                <>
                                  <Button size="sm" onClick={() => void saveLine(line)} disabled={savingLineId === line.id}>
                                    {savingLineId === line.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    Save Line
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingLineId(null)} disabled={savingLineId === line.id}>
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => startLineEdit(line)}>
                                  Edit Line
                                </Button>
                              )}
                            </div>
                          ) : null}
                          {line.notes ? <div>{line.notes}</div> : <span className="text-muted-foreground">-</span>}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid min-w-0 gap-4 xl:grid-cols-[1.6fr_1fr]">
        <Card className="min-w-0">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle>Import History</CardTitle>
            <FileClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="max-h-[360px] space-y-3 overflow-y-auto pr-2">
            {order.importHistory.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No import history recorded.
              </div>
            ) : (
              order.importHistory.map((item) => (
                <div key={item.id} className="rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">Version {item.version_number}</span>
                      {item.import_status ? (
                        <Badge variant="outline" className={toneForStatus(item.import_status)}>
                          {item.import_status}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xs text-muted-foreground">{formatDateTime(item.created_at)}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void openHistoryCsv(item)}
                        disabled={!item.attachment_id}
                      >
                        <Search className="mr-1.5 h-3.5 w-3.5" />
                        View CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void downloadHistoryCsv(item)}
                        disabled={!item.attachment_id}
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        Download CSV
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">{item.file_name || 'Imported file'}</div>
                  <div className="mt-3 grid gap-3 md:grid-cols-5">
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Rows</div><div className="mt-1 text-sm font-medium">{item.rows_total ?? 0}</div></div>
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Added</div><div className="mt-1 text-sm font-medium">{item.rows_added ?? 0}</div></div>
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Updated</div><div className="mt-1 text-sm font-medium">{item.rows_updated ?? 0}</div></div>
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Removed</div><div className="mt-1 text-sm font-medium">{item.rows_removed ?? 0}</div></div>
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Exceptions</div><div className="mt-1 text-sm font-medium">{item.rows_exception ?? 0}</div></div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Fulfillment Trace</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[360px] space-y-3 overflow-y-auto pr-2">
            {order.fulfillments.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No fulfillment records yet.
              </div>
            ) : (
              order.fulfillments.map((item) => (
                <div key={item.id} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium">{item.fulfillment_type || 'FULFILLMENT'}</div>
                    <div className="text-xs text-muted-foreground">{formatDate(item.created_at)}</div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {item.reference_number || 'No reference'}
                  </div>
                  <div className="mt-2 text-sm font-medium">Qty {item.qty_fulfilled ?? 0}</div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
      </div>
      </div>
    </div>
    <Dialog open={csvViewer.open} onOpenChange={(open) => setCsvViewer((prev) => ({ ...prev, open }))}>
      <DialogContent dock="right" className="flex h-[min(88vh,860px)] w-[min(100vw-32px,1024px)] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{csvViewer.title}</DialogTitle>
          <DialogDescription>Imported CSV source for this PO version.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {csvViewer.loading ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : csvViewer.error ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {csvViewer.error}
            </div>
          ) : (
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="bg-muted/50">
                  {csvViewer.headers.map((header, index) => (
                    <th key={`${header}-${index}`} className="whitespace-nowrap border px-2 py-1 text-left font-medium">
                      {header || `Column ${index + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvViewer.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b">
                    {csvViewer.headers.map((_header, cellIndex) => (
                      <td key={cellIndex} className="whitespace-nowrap border px-2 py-1">
                        {row[cellIndex] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function WmsPurchaseOrderDetailPage() {
  return <WmsPurchaseOrderDetailPanel />;
}
